import { styled, css, ITheme } from '@unfold/theme';

import { ITypographyProps, Variant, TCustomColor } from './types';
import { DEFAULT_TEXT_ALIGN, noWrapCss } from './constants';
import { getColor } from './utils';
import { isString } from '@unfold/utils';

export const Small = styled.small``;
Small.displayName = 'Small';

export const Italic = styled.em``;
Italic.displayName = 'Italic';

export const Strong = styled.strong``;
Strong.displayName = 'Strong';

export const Link = styled.a``;
Link.displayName = 'Link';

export const Strikethrough = styled.s``;
Strikethrough.displayName = 'Strikethrough';

const linkCSS = (theme: ITheme, customColor?: TCustomColor): string => `
  a {
    text-decoration: none;
    color: ${getColor('a', theme, customColor)};
    cursor: pointer;

    :hover {
      color: ${getColor('a:hover', theme, customColor)};
    }
  }
`;

const baseTypoCSSReset = css`
  display: block;
  margin: 0;
  padding: 0;
  letter-spacing: 0;
`;

const H100 = styled.span<ITypographyProps>`
  ${baseTypoCSSReset}
  font-weight: 700;
  font-size: 11px;
  line-height: 16px;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H100, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H100.displayName = Variant.H100;

const H200 = styled.span<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H200, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H200.displayName = Variant.H200;

const H300 = styled.span<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-transform: uppercase;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H300, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H300.displayName = Variant.H300;

const H400 = styled.h6<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.003em;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H400, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H400.displayName = Variant.H400;

const H500 = styled.h5<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.006em;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H500, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H500.displayName = Variant.H500;

const H600 = styled.h4<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.008em;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H600, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H600.displayName = Variant.H600;

const H700 = styled.h3<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 24px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.2px;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H700, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H700.displayName = Variant.H700;

const H800 = styled.h2<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 29px;
  font-weight: 500;
  line-height: 32px;
  letter-spacing: 0.2px;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H800, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H800.displayName = Variant.H800;

const H900 = styled.h1<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 35px;
  font-weight: 500;
  line-height: 40px;
  letter-spacing: 0.2px;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H900, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
  ${({ theme, customColor }): string => linkCSS(theme, customColor)}
`;
H900.displayName = Variant.H900;

const Code = styled.code<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 12px;
  white-space: pre-wrap;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme, customColor }): string =>
    getColor(Variant.Code, theme, customColor)};
  font-family: 'SFMono-Medium', 'SF Mono', 'Segoe UI Mono', 'Roboto Mono',
    'Ubuntu Mono', Menlo, Consolas, Courier, monospace;
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
`;
Code.displayName = Variant.Code;

const getFontSize = (fontSize: string | number | undefined = 14): string => {
  return isString(fontSize) ? fontSize : `${fontSize}px`;
};

const getLineHeight = (lineHeight: string | number | undefined = 20): string => {
  return isString(lineHeight) ? lineHeight : `${lineHeight}px`;
};

const Paragraph = styled.p<ITypographyProps>`
  ${baseTypoCSSReset}
  width: auto;
  height: auto;
  font-weight: 400;
  font-size: ${({ fontSize }): string => getFontSize(fontSize)};
  line-height: ${({ lineHeight }): string => getLineHeight(lineHeight)};
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.Paragraph, theme, customColor)};
  strong {
    display: inline;
    font-weight: 600;
  }
  small {
    display: inline;
    margin: 0;
    font-weight: 400;
    font-size: 11px;
    line-height: 16px;
    color: ${({ theme, customColor }): string =>
      getColor('p>small', theme, customColor)};
  }
  em {
    font-size: 14px;
    font-style: italic;
  }
  s {
    text-decoration: line-through;
  }

  ${({ theme }): string => linkCSS(theme)}
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
`;
Paragraph.displayName = Variant.Paragraph;

const Label = styled.label<ITypographyProps>`
  ${baseTypoCSSReset}
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: ${({ align }): string => align ?? DEFAULT_TEXT_ALIGN};
  color: ${({ theme, customColor }): string =>
    getColor(Variant.H100, theme, customColor)};
  ${({ noWrap }): string => (noWrap ? noWrapCss : '')};
`;
Label.displayName = 'Label';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const typography = new Map<Variant, any>();
typography.set(Variant.H100, H100);
typography.set(Variant.H200, H200);
typography.set(Variant.H300, H300);
typography.set(Variant.H400, H400);
typography.set(Variant.H500, H500);
typography.set(Variant.H600, H600);
typography.set(Variant.H700, H700);
typography.set(Variant.H800, H800);
typography.set(Variant.H900, H900);
typography.set(Variant.Code, Code);
typography.set(Variant.Paragraph, Paragraph);
typography.set(Variant.Label, Label);

/* eslint-disable-next-line @typescript-eslint/no-explicit-any*/
export const getTypographyComponent = (variant: Variant): any =>
  typography.get(variant);
